import React from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { ISanityImage } from '@rbi-ctg/menu';
import { Allergens } from 'components/nutrition/allergens';
import NutritionModal from 'components/nutrition/index.bk';
import { AllergenGroup, NutritionFacts } from 'components/nutrition/types';
import { getAllergenGroups } from 'components/nutrition/utils';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { formatCalories, formatJoules } from 'utils/menu';

import { Card, Title } from './card';
import { IProduct } from './types';

const Body = styled.div`
  font-family: ${Styles.fontFamily.body};
  text-transform: ${Styles.textTransform.body};
  color: ${Styles.color.grey.one};
  margin-top: 10px;

  ${Styles.desktop`
    margin-top: 4px;
  `}
`;

const Calories = styled.p`
  margin: 0;
  font-size: 0.85rem;
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 8px 12px 0;

  ${Styles.desktop`
    padding: 12px 16px 0;
  `}
`;

interface IProductCard {
  product: IProduct;
  backgroundImage?: ISanityImage;
}

export const ProductCard: React.FC<IProductCard> = ({ product, backgroundImage }) => {
  const { formatMessage } = useIntl();
  const { region } = useLocale();
  const enableAllergens = useFlag(LaunchDarklyFlag.ENABLE_NUTRITION_EXPLORER_ALLERGENS);
  const enableEUNutritionDisplay = useFlag(LaunchDarklyFlag.ENABLE_EU_NUTRITION_DISPLAY);
  const enableNutritonDecimals = useFlag(LaunchDarklyFlag.ENABLE_NUTRITION_DECIMALS);

  const formattedCalories =
    product.nutrition &&
    formatCalories(
      product.nutrition.calories,
      formatMessage({ id: 'cal' }),
      region,
      enableNutritonDecimals
    );

  const formattedJoules = formatJoules(product.nutrition.calories, j => Number(j.toFixed(1)));

  const calories = enableEUNutritionDisplay ? (
    <>
      {formattedCalories} / {formattedJoules}
    </>
  ) : (
    formattedCalories
  );

  let allergenGroups: AllergenGroup[] | undefined;
  if (enableAllergens && product.allergens) {
    allergenGroups = getAllergenGroups(product.allergens, formatMessage);
  }

  const nutritionFacts: NutritionFacts[] = [
    {
      itemName: product.name,
      itemImage: product.image,
      facts: product.nutrition || {},
      allergenGroups,
    },
  ];

  const nutritionInfoCard = (
    <Card key={product._id} image={product.image} imageAlt="" backgroundImage={backgroundImage}>
      <Content>
        <Title useFullHeight>{product.name}</Title>
        <Body>
          {calories && <Calories>{calories}</Calories>}
          {allergenGroups && <Allergens allergenGroups={allergenGroups} />}
        </Body>
      </Content>
    </Card>
  );

  return <NutritionModal nutritionFacts={nutritionFacts} activatingElement={nutritionInfoCard} />;
};
